import Phaser from 'phaser';
import { createRng } from '@rabbit2/shared/sim';
import { RunScene } from './scenes/RunScene.ts';
import { VilaScene } from './scenes/VilaScene.ts';
import { CoopScene } from './scenes/CoopScene.ts';
import { installTestApi, markReady, urlSeed, urlStress } from './testapi.ts';
import { acordarAudio, alternarMudo, estaMudo, iniciarMusica, sfx } from './audio.ts';

const seed = urlSeed();
const stress = urlStress();
const params = new URLSearchParams(location.search);
const runMode = params.get('run') === '1';
const vilaMode = params.get('vila') === '1';
const coopMode = params.get('coop') === '1';
const speed = Number(params.get('speed') ?? 1) || 1;

/** AC-M0-06: cena de stress — N retângulos quicando, movimento manual (sem física) */
class StressScene extends Phaser.Scene {
  #rects: { r: Phaser.GameObjects.Rectangle; vx: number; vy: number }[] = [];

  constructor() {
    super('stress');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1A1633');
    const rng = createRng(seed, 7);
    const cores = [0xff8a3d, 0x7c5cff, 0xffd166, 0xbfd7ea, 0xff5d8f];
    for (let i = 0; i < stress; i++) {
      const r = this.add.rectangle(
        rng.int(16, 944), rng.int(16, 524), 16, 16,
        cores[rng.int(0, cores.length - 1)] ?? 0xffffff,
      );
      this.#rects.push({ r, vx: (rng.int(0, 200) - 100) / 2, vy: (rng.int(0, 200) - 100) / 2 });
    }
    markReady(this.game);
  }

  override update(_t: number, dtMs: number): void {
    const dt = dtMs / 1000;
    for (const o of this.#rects) {
      o.r.x += o.vx * dt;
      o.r.y += o.vy * dt;
      if (o.r.x < 8 || o.r.x > 952) o.vx = -o.vx;
      if (o.r.y < 8 || o.r.y > 532) o.vy = -o.vy;
    }
  }
}

/** carrega a arte (SVG → textura) e roteia para a cena certa. */
class PreloadScene extends Phaser.Scene {
  constructor() {
    super('preload');
  }

  preload(): void {
    this.cameras.main.setBackgroundColor('#1A1633');
    this.add.text(480, 250, 'RABBIT 2.0', { fontFamily: 'sans-serif', fontSize: '48px', color: '#FFF3E0', fontStyle: 'bold' }).setOrigin(0.5);
    this.add.text(480, 300, 'acendendo o luar…', { fontFamily: 'monospace', fontSize: '16px', color: '#BFD7EA' }).setOrigin(0.5);
    const A = 'art/';
    this.load.svg('pip', `${A}pip.svg`, { width: 60, height: 68 });
    this.load.svg('pip2', `${A}pip2.svg`, { width: 60, height: 68 });
    this.load.svg('sonequito', `${A}sonequito.svg`, { width: 42, height: 42 });
    this.load.svg('lencolzinho', `${A}lencolzinho.svg`, { width: 42, height: 42 });
    this.load.svg('abobrinha', `${A}abobrinha.svg`, { width: 46, height: 46 });
    this.load.svg('robo', `${A}robo.svg`, { width: 44, height: 44 });
    this.load.svg('escaravelho', `${A}escaravelho.svg`, { width: 46, height: 46 });
    this.load.svg('golemita', `${A}golemita.svg`, { width: 96, height: 96 });
    this.load.svg('norno', `${A}norno.svg`, { width: 96, height: 96 });
    this.load.svg('palhaco', `${A}palhaco.svg`, { width: 60, height: 68 });
    this.load.svg('gem', `${A}gem.svg`, { width: 18, height: 22 });
    this.load.svg('moon', `${A}moon.svg`, { width: 220, height: 220 });
    this.load.svg('star', `${A}star.svg`, { width: 14, height: 14 });
  }

  create(): void {
    if (coopMode) this.scene.start('coop', { seed, players: Number(params.get('players') ?? 2) || 2 });
    else if (stress > 0) this.scene.start('stress');
    else if (runMode) this.scene.start('run', { seed, speed, chapter: Number(params.get('chapter') ?? 1) || 1 });
    else if (vilaMode) this.scene.start('vila', { total: Number(params.get('total') ?? 0) || 0 });
    else this.scene.start('boot');
  }
}

class BootScene extends Phaser.Scene {
  constructor() {
    super('boot');
  }

  /** botão clicável/tocável (≥48px, ACC-1) que inicia uma cena. */
  #botao(y: number, rotulo: string, cor: number, aoTocar: () => void): void {
    const w = 300;
    const h = 60;
    const bg = this.add.rectangle(480, y, w, h, cor, 1).setStrokeStyle(3, 0x1a1633).setInteractive({ useHandCursor: true });
    const txt = this.add.text(480, y, rotulo, { fontFamily: 'sans-serif', fontSize: '24px', color: '#1A1633', fontStyle: 'bold' }).setOrigin(0.5);
    const acender = (on: boolean): void => { bg.setScale(on ? 1.05 : 1); txt.setScale(on ? 1.05 : 1); };
    const agir = (): void => { acordarAudio(); iniciarMusica(); sfx('click'); aoTocar(); };
    bg.on('pointerover', () => acender(true)).on('pointerout', () => acender(false));
    bg.on('pointerup', agir);
    txt.setInteractive({ useHandCursor: true }).on('pointerup', agir);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1A1633');
    const chapter = Number(new URLSearchParams(location.search).get('chapter') ?? 1) || 1;
    // céu estrelado + lua (Moonlit Storybook)
    const rng = createRng(seed, 11);
    const céu = this.add.graphics();
    for (let i = 0; i < 120; i++) {
      const grande = rng.next01() < 0.12;
      céu.fillStyle(grande ? 0xfff176 : 0xfff3c4, 0.25 + rng.next01() * 0.6);
      céu.fillCircle(rng.next01() * 960, rng.next01() * 540, grande ? 2.2 : 1);
    }
    if (this.textures.exists('moon')) this.add.image(150, 120, 'moon').setDisplaySize(150, 150).setAlpha(0.95);
    if (this.textures.exists('pip')) this.add.image(760, 330, 'pip').setDisplaySize(150, 168);
    this.add.text(480, 130, 'RABBIT 2.0', { fontFamily: 'sans-serif', fontSize: '64px', color: '#FFF3E0', fontStyle: 'bold' }).setOrigin(0.5).setShadow(0, 3, '#7c5cff', 8);
    this.add.text(480, 190, 'A Noite Sem Fim', { fontFamily: 'sans-serif', fontSize: '28px', color: '#BFD7EA' }).setOrigin(0.5);

    this.#botao(280, '▶  Jogar', 0xff8a3d, () => this.scene.start('run', { seed, speed, chapter }));
    this.#botao(355, '🏡  Vila', 0xffd166, () => this.scene.start('vila', { total: Number(params.get('total') ?? 0) || 0 }));
    this.#botao(430, '👥  Co-op', 0xbfd7ea, () => this.scene.start('coop', { seed, players: 2 }));

    this.add.text(480, 500, 'WASD/setas movem · ataque automático · Enter para jogar · M muta', {
      fontFamily: 'monospace', fontSize: '15px', color: '#9AA5B1',
    }).setOrigin(0.5);
    // botão de som (mudo)
    const som = this.add.text(918, 24, estaMudo() ? '🔇' : '🔊', { fontSize: '26px' })
      .setOrigin(0.5).setInteractive({ useHandCursor: true });
    const toggleMudo = (): void => { acordarAudio(); som.setText(alternarMudo() ? '🔇' : '🔊'); };
    som.on('pointerup', toggleMudo);
    this.input.keyboard?.on('keydown-M', toggleMudo);

    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-ENTER', () => this.scene.start('run', { seed, speed, chapter }));
      kb.on('keydown-SPACE', () => this.scene.start('run', { seed, speed, chapter }));
    }

    markReady(this.game);
  }
}

// PreloadScene (1ª) carrega a arte e roteia; todas as cenas ficam registradas
const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game',
  width: 960,
  height: 540, // resolução virtual (doc 07 §2)
  scene: [PreloadScene, BootScene, RunScene, VilaScene, CoopScene, StressScene],
});

game.registry.set('seed', seed);
// sanidade do RNG compartilhado no browser (cross-check do AC-M0-02 vem no Playwright)
createRng(seed).nextU32();
installTestApi(game, seed);
